import request from "@libs/request/request";
import { PriceFormatter } from "@libs/helper/PriceFormatter";
import { useFavorite } from "src/hooks/FavoriteContext";
import { Image, Button, Tooltip, Rate, Popover } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import DateHelper from "@libs/helper/DateHelper";
import { IoCalendarOutline, IoCheckmarkOutline } from "react-icons/io5";
import { FiMapPin } from "react-icons/fi";
import CustomImage from "@components/CustomImage/CustomImage";
import { FiInfo } from "react-icons/fi";

const OfferImage = dynamic(() => import("./OfferImage"), {
  ssr: false,
});
const FavoriteModal = dynamic(() => import("./FavoriteModal"), {
  ssr: false,
});

const OfferItem = ({ item, index, router, isMobile, onHostPage }) => {
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [includedPriceOptions, setIncludedPriceOptions] = useState([]);
  const { t, i18n } = useTranslation("common");

  const { favoriteOffers, setFavoriteOffers } = useFavorite();

  const isRecc = item?.highlighted == 1;
  const isLiked = favoriteOffers?.ids?.includes(item?.uuid) || false;
  const isEn = i18n.language === "en";
  const imgLoading = index <= 3 ? "eager" : "lazy";
  const rate = item?.rate?.rate;
  const dataCurrency = item?.basic_room?.price_currency || "EUR";

  const formatDates = (date) =>
    moment(date, "YYYY-MM-DD").format(isEn ? "DD/MM/YYYY" : "DD.MM.YYYY");
  const priceDisplay = (price) =>
    PriceFormatter(price, dataCurrency, i18n.language, true);

  useEffect(() => {
    if (isEn) {
      setIncludedPriceOptions(
        item?.options?.en?.length > 0 ? item?.options?.en : []
      );
    } else {
      setIncludedPriceOptions(
        item?.options?.de?.length > 0 ? item?.options?.de : []
      );
    }
  }, [item?.options]);

  const renderDate = () => {
    const offerType = item?.date_type;

    if (offerType === "year") {
      return t("offer.text_year_date");
    }

    const list = [];
    item?.dates?.map((d, i) => {
      if (i == 0) {
        return list.push(
          t("open_from") +
            DateHelper.dateFormat(d?.start_date) +
            " " +
            t("until") +
            " " +
            DateHelper.dateFormat(d?.end_date)
        );
      } else {
        return list.push(
          t("open_from").toLowerCase() +
            DateHelper.dateFormat(d?.start_date) +
            " " +
            t("until") +
            " " +
            DateHelper.dateFormat(d?.end_date)
        );
      }
    });

    if (list.length > 2) {
      return item?.dates?.length + " " + t("package_date_text");
    }
    return list.join(", " + t("and") + " " + t("opened"));
  };

  const handleUpdateFavoriteOffersList = async (id) => {
    const updatedIds = favoriteOffers.ids.includes(id)
      ? favoriteOffers.ids.filter((id) => id !== id)
      : [...favoriteOffers.ids, id];

    setFavoriteOffers({
      ...favoriteOffers,
      ids: updatedIds,
    });

    const response = await request.get("user/profile/wishlist/item/ids");
    setFavoriteOffers(response?.data);
  };

  return (
    <article
      className={`w-full grid grid-cols-1 md:grid-cols-6 md:h-[275px] shadow-base rounded-2xl relative ${
        isRecc
          ? "!border-2 !border-green !rounded-tr-none mt-4"
          : "overflow-hidden"
      } ${isRecc && index === 1 && "!mt-0"}`}
    >
      {isRecc && (
        <div className="absolute -right-[2px] -top-[33px] border-2 border-green px-3 h-[33px] rounded-t-2xl flex flex-col items-center justify-center bg-white">
          <h3 className="text-base font-bold text-center whitespace-nowrap">
            {t("recommended_by_moverii")}
          </h3>
        </div>
      )}
      {/* Image */}
      <div className="col-span-1 md:col-span-2 overflow-hidden md:rounded-l-[14px] relative">
        <OfferImage
          item={item}
          isLiked={isLiked}
          imgLoading={imgLoading}
          isEn={isEn}
          isRecc={isRecc}
          setShowFavoriteModal={setShowFavoriteModal}
          handleUpdateFavoriteOffersList={handleUpdateFavoriteOffersList}
          isMobile={isMobile}
        />
      </div>

      {/* content */}
      <a
        href={item?.url_name ? `/offer/${item?.url_name}` : "!#"}
        target={item?.url_name ? "_blank" : "_self"}
        className="col-span-1 md:col-span-3 flex flex-col justify-between p-3"
      >
        <h2 className="font-bold md:text-lg text-[20px] md:!line-clamp-2 w-full">
          {item?.title?.en || item?.title?.de
            ? isEn
              ? item?.title?.en || item?.title?.de
              : item?.title?.de || item?.title?.en
            : item?.title}
        </h2>

        <div className="flex flex-col gap-2 mt-4 md:mt-0">
          {(item?.destination?.en || item?.destination?.de) && (
            <div className="flex items-center gap-1">
              <div className="flex">
                <FiMapPin className="text-[#999999] text-[18px]" />
              </div>
              <p className="md:line-clamp-2 text-base pl-[2px]">
                {isEn
                  ? item?.destination?.en ?? item?.destination?.de
                  : item?.destination?.de ?? item?.destination?.en}
              </p>
            </div>
          )}
          <div className="flex items-center gap-1 pl-[2px]">
            <div className="flex">
              <IoCalendarOutline className="text-[#999999] text-[18px]" />
            </div>
            <p className="line-clamp-3 md:line-clamp-2 text-base pl-[1.5px]">
              {renderDate()}
            </p>
          </div>
        </div>

        <div className="w-full flex flex-col gap-y-1 mt-4 md:mt-0">
          {item?.review?.content &&
          item?.review?.content !== "" &&
          item?.review?.rate >= 4 ? (
            <Tooltip
              overlayClassName="md:!w-[450px] md:!min-w-[450px] md:!max-w-[450px] !w-[300px] !min-w-[300px] !max-w-[300px]"
              title={
                <div className="flex flex-col items-start justify-start gap-2 p-2">
                  <p
                    className="flex text-black cursor-pointer text-base text-left"
                    dangerouslySetInnerHTML={{
                      __html: `"${item?.review?.content}"`,
                    }}
                  />
                  <p className="text-gray-50 italic text-base text-left font-medium">
                    -{" "}
                    {item?.review?.name === "Guest"
                      ? t("Anonymous")
                      : item?.review?.name ?? t("Anonymous")}
                  </p>
                </div>
              }
            >
              <div className="flex flex-col">
                <p
                  className="flex text-black !line-clamp-3 cursor-pointer text-base"
                  dangerouslySetInnerHTML={{
                    __html: `"${item?.review?.content}"`,
                  }}
                />
                <p className="!line-clamp-1 text-gray-50 italic text-base">
                  -{" "}
                  {item?.review?.name === "Guest"
                    ? t("Anonymous")
                    : item?.review?.name ?? t("Anonymous")}
                </p>
              </div>
            </Tooltip>
          ) : (
            <></>
          )}
        </div>
      </a>

      {/* right */}
      <a
        href={item?.url_name ? `/offer/${item?.url_name}` : "!#"}
        target={item?.url_name ? "_blank" : "_self"}
        className="col-span-1 p-3 md:px-1.5 md:py-3 md:border-l h-full flex flex-col justify-between items-end border-t md:border-t-0"
      >
        {item?.rate?.rate && item?.rate?.type === "moverii" ? (
          <div className="w-full flex md:flex-col gap-2 md:gap-1 md:justify-between items-center md:items-end">
            <div className="md:w-full flex items-center md:justify-end gap-2 md:gap-1">
              <div className="md:hidden lg:flex">
                <CustomImage
                  src="static/logo/moverii_logo_m.svg"
                  alt="moverii.de"
                  loading={imgLoading}
                  width={15}
                  height={15}
                  urlWidth={30}
                  format="webp"
                />
              </div>
              <Rate
                allowHalf
                disabled
                style={{
                  fontSize: onHostPage
                    ? item?.rate?.num_reviews?.toString()?.length == 2
                      ? "13.5px"
                      : item?.rate?.num_reviews?.toString()?.length >= 3
                      ? "12.5px"
                      : "14px"
                    : "15px",
                }}
                className="-mt-1 md:mt-0"
                value={
                  rate >= 1.25 && rate <= 1.749
                    ? 1.5
                    : rate >= 1.75 && rate <= 2
                    ? 2
                    : rate >= 2.25 && rate <= 2.749
                    ? 2.5
                    : rate >= 2.75 && rate <= 3
                    ? 3
                    : rate >= 3.25 && rate <= 3.749
                    ? 3.5
                    : rate >= 3.75 && rate <= 4
                    ? 4
                    : rate >= 4.25 && rate <= 4.749
                    ? 4.5
                    : rate >= 4.75 && rate <= 5
                    ? 5
                    : rate
                }
              />

              {item?.rate?.num_reviews && (
                <p className="text-gray-50 text-xs self-center font-semibold">
                  ({item?.rate?.num_reviews})
                </p>
              )}
            </div>
            <p className="text-gray-50 text-sm md:text-[12px] text-right">
              {t("offer.moverii_traveler_ratings")}
            </p>
          </div>
        ) : (
          <div className="md:h-[42px] md:w-full" />
        )}

        {/* price */}
        <div className="w-full flex md:flex-col gap-2 md:gap-1 items-start md:items-end my-3 md:my-0">
          <p className="text-gray-50 text-sm hidden md:block">{t("from")}</p>
          <div className="font-bold flex gap-2 md:gap-0 lg:gap-2 flex-row md:flex-col-reverse lg:flex-row">
            <p className="text-gray-50 text-base md:hidden font-normal pt-[3.5px]">
              {t("from")}
            </p>
            {item?.basic_room?.discount > 0 && (
              <p className="text-[#E43A36] text-base hidden md:block">
                {priceDisplay(item?.basic_room?.minimum_price_after_discount)}
              </p>
            )}
            <p
              className={`${
                item?.basic_room?.discount > 0 ? "!line-through" : ""
              } text-lg md:text-base`}
            >
              {priceDisplay(item?.basic_room?.minimum_price_per_people)}
            </p>
            {item?.basic_room?.discount > 0 && (
              <p className="text-[#E43A36] text-lg md:hidden">
                {priceDisplay(item?.basic_room?.minimum_price_after_discount)}
              </p>
            )}
          </div>
          <p className="text-base md:text-sm text-gray-50">
            {t("offer.for")}{" "}
            <strong className="font-bold text-black text-lg md:text-base">
              {item?.basic_room?.duration || "0"}
            </strong>{" "}
            {t("dayss")}
          </p>
        </div>

        {!isMobile && includedPriceOptions?.length > 0 && (
          <Popover
            overlayClassName="popover-transparent rounded-2xl py-4 arrow-greenblue"
            trigger="hover"
            placement="topRight"
            content={
              <div className="flex flex-col bg-lightBlueGreen p-4 pb-0 rounded-2xl shadow-md w-90 px-6">
                <div className="font-bold flex row justify-between">
                  <h4 className="flex text-base">{t("offer.PriceInclude")}</h4>
                </div>
                <div className="flex flex-col space-y-2 my-3">
                  {includedPriceOptions?.map((item, i) => {
                    if (item !== "") {
                      return (
                        <div
                          key={"offer-included-price-" + i}
                          className="text-grayDark flex flex-row space-x-1 items-top"
                        >
                          <div className="flex self-center">
                            <IoCheckmarkOutline className="text-orange text-2xl" />
                          </div>
                          <p
                            className="w-11/12 text-sm"
                            style={{ whiteSpace: "pre-line" }}
                            dangerouslySetInnerHTML={{
                              __html: item,
                            }}
                          />
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            }
          >
            <div className="flex items-center gap-1 cursor-pointer">
              <p
                className={`underline text-[13px] text-base md:text-[13px] text-right`}
              >
                {t("included_services")}
              </p>
              <div className="-mb-[2px] flex">
                <FiInfo className="text-xs text-black" />
              </div>
            </div>
          </Popover>
        )}

        <Button
          className="w-full !bg-orange !h-[35px] p-0 font-bold !text-white rounded-xl text-base hidden md:block"
          onClick={() =>
            item?.url_name ? router.push(`/offer/${item?.url_name}`) : {}
          }
        >
          {t("search.details")}
        </Button>
      </a>

      {showFavoriteModal && (
        <FavoriteModal
          open={showFavoriteModal}
          setOpen={setShowFavoriteModal}
          offerId={item?.uuid}
          handleUpdateFavoriteOffersList={handleUpdateFavoriteOffersList}
        />
      )}
    </article>
  );
};

export default OfferItem;
